import FadeIn from "@/common/components/fade-in";
import SEO from "@/common/components/seo";
import { AuthenticationProvider } from "@/common/providers/authentication";
import { useSiteSettings } from "@/common/providers/site-settings";
import { Button } from "@/components/ui/button";
import { LoginRoute } from "@/features/authentication/route";
import { MerchRoute } from "@/features/home/route";
import { CheckoutCartRoute } from "@/features/payments/route";
import { formatPrice } from "@/lib/string";
import { cn } from "@/lib/utils";
import { X } from "lucide-react";
import { useEffect, useMemo } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import useShipping from "../hooks/use-shipping";
import { CartItem } from "../models/cart";
import { useCart } from "../providers/cart";

function CartLineItem({
	item,
	onIncrement,
	onDecrement,
	onRemove,
}: {
	item: CartItem;
	onIncrement: () => void;
	onDecrement: () => void;
	onRemove: () => void;
}) {
	const baseURL = import.meta.env.VITE_SERVER_API_URL;
	const imageUrl = item.productImageUrl
		? `${baseURL}/common/uploads/${item.productImageUrl}`
		: undefined;

	const attrs = (item.attributes || [])
		.map((a) => `${a.attributeType}: ${a.value}`)
		.join(" · ");

	const unitPrice = Number(item.basePrice ?? 0) + Number(item.priceModifier ?? 0);

	return (
		<div className="flex gap-4 rounded-[32px] bg-white p-4 ring-1 ring-black/5">
			<div
				className={cn(
					"h-22 w-22 shrink-0 rounded-[18px] bg-cover bg-center",
					imageUrl ? "" : "bg-gray-100"
				)}
				style={imageUrl ? { backgroundImage: `url('${imageUrl}')` } : undefined}
			/>

			<div className="flex min-w-0 flex-1 flex-col gap-3">
				<div className="flex items-start justify-between gap-2">
					<div className="min-w-0">
						<p className="web-body-b1-medium truncate">{item.productName}</p>
						<p className="web-body-b2 text-gray-700">{item.variantName}</p>
						{attrs && <p className="web-body-b3 text-gray-700">{attrs}</p>}
					</div>
					<button
						className="rounded-full p-2 text-gray-500 hover:bg-gray-100"
						onClick={onRemove}
						aria-label="Remove item"
					>
						<X className="h-4 w-4" />
					</button>
				</div>

				<div className="flex items-center justify-between gap-3">
					<p className="web-body-b1-medium text-blue-700">
						{formatPrice(unitPrice, "AUD")}
					</p>

					<div className="flex items-center gap-2">
						<Button
							variant="secondary"
							className="h-9 w-9 rounded-full p-0"
							onClick={onDecrement}
							disabled={item.quantity <= 1}
						>
							-
						</Button>
						<span className="web-body-b1 w-6 text-center">
							{item.quantity}
						</span>
						<Button
							variant="secondary"
							className="h-9 w-9 rounded-full p-0"
							onClick={onIncrement}
						>
							+
						</Button>
					</div>
				</div>
			</div>
		</div>
	);
}

export default function MerchCartPage() {
	const navigate = useNavigate();
	const location = useLocation();
	const { settings, isLoading: isSettingsLoading } = useSiteSettings();
	const cartEnabled = settings.storeEnabled && settings.cartEnabled;
	const isAuthenticated = AuthenticationProvider.isAuthenticated;

	const { items, itemCount, subtotal, setQuantity, removeItem, clear, isLoading } =
		useCart();
	const { quote: shippingQuote } = useShipping(subtotal);
	const shippingFee = shippingQuote?.amount ?? 0;
	const total = subtotal + shippingFee;

	useEffect(() => {
		const queryParams = new URLSearchParams(location.search);
		const sessionId = queryParams.get("session_id");
		if (sessionId) {
			toast.success("Order placed successfully.");
		}
	}, [location.search]);

	const subtotalLabel = useMemo(() => formatPrice(subtotal, "AUD"), [subtotal]);

	const onCheckout = () => {
		if (!isAuthenticated) {
			const params = new URLSearchParams();
			params.set("from", location.pathname);
			navigate(LoginRoute + "?" + params.toString());
			return;
		}
		navigate(CheckoutCartRoute);
	};

	if (!isSettingsLoading && !cartEnabled) {
		return (
			<div className="min-h-screen bg-white">
				<SEO
					title="Cart - Reel Rewards"
					description="The Reel Rewards cart is currently unavailable."
				/>
				<FadeIn>
					<div className="flex flex-col items-center gap-6 px-4 py-20 text-center md:px-37">
						<p className="web-heading-h3">Cart is currently unavailable</p>
						<p className="web-body-b1 text-gray-700">
							We're doing some updates behind the scenes.
						</p>
						<div className="mt-2 flex w-full justify-center">
							<Button asChild className="w-full md:w-44">
								<Link to={MerchRoute}>Back to store</Link>
							</Button>
						</div>
					</div>
				</FadeIn>
			</div>
		);
	}

	const isEmpty = items.length === 0;
	const titleStyle = {
		background:
			"linear-gradient(161deg, #0FF1E5 3.9%, #0DAAEC 33.24%, #0823F8 72.3%, #000D74 116.02%)",
		WebkitBackgroundClip: "text",
		backgroundClip: "text",
		WebkitTextFillColor: "transparent",
	} as const;

	return (
		<div className="min-h-screen bg-white">
			<SEO
				title="Cart - Reel Rewards"
				description="Review your Reel Rewards store cart."
			/>

			<FadeIn>
				<div className="px-4 py-12 md:px-37 md:py-20">
					<div className="flex flex-col gap-12">
						<div className="flex items-end justify-between gap-6">
							<div className="flex flex-col gap-2">
								<p className="web-heading-h2 text-black">
									Your <span style={titleStyle}>Cart</span>
								</p>
								<p className="web-body-b1 text-gray-700">
									{isLoading
										? "Loading…"
										: `${itemCount} item${itemCount === 1 ? "" : "s"}`}
								</p>
							</div>
							<Link className="web-body-b1 text-blue-700" to={MerchRoute}>
								Back to store
							</Link>
						</div>

						{isLoading ? (
							<div className="rounded-[32px] bg-white p-8 text-center ring-1 ring-black/5">
								<p className="web-body-b1 text-gray-700">Loading cart…</p>
							</div>
						) : isEmpty ? (
							<div className="rounded-[32px] bg-white p-10 text-center ring-1 ring-black/5">
								<p className="web-body-b1-medium">Your cart is empty</p>
								<p className="web-body-b1 text-gray-700">
									Add some items and come back here.
								</p>
								<div className="mt-6 flex justify-center">
									<Button asChild>
										<Link to={MerchRoute}>Browse store</Link>
									</Button>
								</div>
							</div>
						) : (
							<div className="flex flex-col gap-6 md:flex-row md:items-start">
								<div className="flex flex-1 flex-col gap-4">
									{items.map((item) => (
										<CartLineItem
											key={item.variantId}
											item={item}
											onIncrement={() =>
												setQuantity(item.variantId, item.quantity + 1)
											}
											onDecrement={() =>
												setQuantity(item.variantId, item.quantity - 1)
											}
											onRemove={() => removeItem(item.variantId)}
										/>
									))}

									<button
										className="web-body-b1 w-fit text-blue-700 hover:text-blue-900"
										onClick={() => clear()}
									>
										Clear cart
									</button>
								</div>

								<div className="w-full rounded-[32px] bg-white p-6 ring-1 ring-black/5 md:w-[360px]">
									<div className="flex items-center justify-between">
										<p className="web-body-b1 text-gray-700">Subtotal</p>
										<p className="web-body-b1-medium text-black">
											{subtotalLabel}
										</p>
									</div>
									<div className="mt-3 flex items-center justify-between">
										<p className="web-body-b1 text-gray-700">Shipping</p>
										<p
											className={cn(
												"web-body-b1-medium",
												shippingQuote?.free
													? "text-green-700"
													: "text-black"
											)}
										>
											{shippingQuote?.free
												? "Free"
												: formatPrice(shippingFee, "AUD")}
										</p>
									</div>
									{shippingQuote?.message && (
										<p
											className={cn(
												"web-body-b3 mt-2",
												shippingQuote.free
													? "text-green-700"
													: "text-gray-700"
											)}
										>
											{shippingQuote.message}
										</p>
									)}
									<div className="mt-4 flex items-center justify-between border-t border-gray-100 pt-4">
										<p className="web-body-b1 text-gray-700">Total</p>
										<p className="web-heading-h4 text-blue-700">
											{formatPrice(total, "AUD")}
										</p>
									</div>
									<Button
										className="mt-4 w-full"
										disabled={isLoading || isEmpty}
										onClick={onCheckout}
									>
										Checkout
									</Button>
								</div>
							</div>
						)}
					</div>
				</div>
			</FadeIn>
		</div>
	);
}
